import { useState, useEffect, lazy, Suspense, startTransition } from 'react';
import { apiCall } from '@/App';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Zap, Users, Eye, Heart, Activity, ChevronDown } from 'lucide-react';
import MobileMenu from '@/components/MobileMenu';
import { Helmet } from 'react-helmet-async';

// Bawah lipatan + modal login dimuat sebagai chunk berasingan (main bundle lebih kecil = LCP/TBT lebih baik)
const LandingBelowFold = lazy(() => import('@/components/landing/LandingBelowFold'));
const LoginModal = lazy(() => import('@/components/landing/LoginModal'));

/* ─── Floating Card (CSS keyframes, tiada framer-motion) ─── */
function FloatingCard({ children, className, delay = 0, duration = 4 }) {
  return (
    <div className={`float-card ${className}`} style={{ '--float-dur': `${duration}s`, '--float-delay': `${delay}s` }}>
      {children}
    </div>
  );
}

/* Guna pakai hero statik dari index.html (elemen LCP): kekalkan dalam DOM, jadikan link-nya navigasi SPA, buang bila keluar dari "/" */
let heroRemoveTimer = null;
function useStaticHero() {
  const navigate = useNavigate();
  const [present] = useState(() => typeof document !== 'undefined' && !!document.getElementById('static-hero'));
  useEffect(() => {
    if (!present) return;
    const el = document.getElementById('static-hero');
    if (!el) return;
    clearTimeout(heroRemoveTimer);
    const onClick = (e) => {
      const a = e.target.closest('a[href^="/"]');
      if (a) { e.preventDefault(); navigate(a.getAttribute('href')); }
    };
    el.addEventListener('click', onClick);
    return () => {
      el.removeEventListener('click', onClick);
      // StrictMode jalankan cleanup+mount semula secara segerak: tangguh pembuangan supaya mount semula boleh batalkannya
      if (window.location.pathname.replace(/\/+$/, '') !== '') el.remove();
      else heroRemoveTimer = setTimeout(() => el.remove(), 0);
    };
  }, [present, navigate]);
  return present;
}

/* ════════════════════════════════════════════════════════════════ */
export default function LandingPage() {
  const [loginOpen, setLoginOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [siteSettings, setSiteSettings] = useState({});
  const staticHero = useStaticHero();
  // Bawah lipatan dirender sebagai transition (time-sliced) selepas hero: kurangkan long task/TBT semasa hidrasi
  const [belowFold, setBelowFold] = useState(false);
  useEffect(() => { startTransition(() => setBelowFold(true)); }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    apiCall('get', '/settings/public').then(setSiteSettings).catch(() => {});
  }, []);

  const socials = [siteSettings.social_facebook, siteSettings.social_instagram, siteSettings.social_tiktok, siteSettings.social_telegram].filter(Boolean);

  const serif = "'Newsreader', 'Newsreader Fallback', serif";
  const sans = "'Plus Jakarta Sans', 'Plus Jakarta Sans Fallback', sans-serif";

  return (
    <div className="min-h-screen" style={{ fontFamily: sans }}>
      <Helmet>
        <title>SMM Panel Malaysia Termurah | Flexa - Beli Followers, Likes & Views</title>
        <meta name="description" content="Flexa - Panel SMM terbaik & termurah di Malaysia. Boost followers, likes & views untuk Instagram, TikTok, YouTube, Facebook. Social proof segera untuk bisnes anda, dari RM0.01/1K, auto-delivery 24/7. Daftar percuma hari ini!" />
        <link rel="canonical" href="https://flexaviral.com" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://flexaviral.com" />
        <meta property="og:title" content="Flexa - Panel SMM #1 Malaysia | Termurah & Terpantas" />
        <meta property="og:description" content="Naikkan sosial media anda dengan cara paling mudah & dipercayai. 3,351 servis, delivery 60 saat, harga 10% lebih murah." />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Flexa",
          "url": "https://flexaviral.com",
          "logo": "https://flexaviral.com/flexa-wordmark.png",
          "description": "Panel SMM terbaik di Malaysia",
          "sameAs": socials,
          "contactPoint": {
            "@type": "ContactPoint",
            "contactType": "customer service",
            "email": "support@flexaviral.com",
            "availableLanguage": ["ms", "en"]
          }
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            { "@type": "Question", "name": "Flexa ni apa sebenarnya?", "acceptedAnswer": { "@type": "Answer", "text": "Flexa adalah panel SMM (Social Media Marketing) terbesar di Malaysia dengan lebih 3,351 servis untuk semua platform sosial media termasuk Instagram, TikTok, YouTube, Facebook, Telegram dan banyak lagi. Kami menyediakan servis followers, likes, views, subscribers dengan harga paling murah dan delivery automatik 24/7." } },
            { "@type": "Question", "name": "Macam mana cara nak order?", "acceptedAnswer": { "@type": "Answer", "text": "Sangat mudah! 1) Daftar akaun percuma, 2) Top up balance melalui FPX, GrabPay atau kad kredit, 3) Pilih servis dan masukkan link, 4) Klik order, delivery bermula secara automatik dalam beberapa minit." } },
            { "@type": "Question", "name": "Selamat ke guna Flexa?", "acceptedAnswer": { "@type": "Answer", "text": "100% selamat. Kami tidak memerlukan password akaun anda. Semua delivery dilakukan melalui API rasmi platform. Data anda disulitkan dan privacy terjamin sepenuhnya." } },
            { "@type": "Question", "name": "Berapa lama order sampai?", "acceptedAnswer": { "@type": "Answer", "text": "Purata delivery adalah dalam masa 60 saat hingga beberapa jam bergantung kepada jenis servis. Kebanyakan order Instagram dan TikTok bermula dalam masa kurang dari 5 minit." } },
            { "@type": "Question", "name": "Ada diskaun untuk reseller?", "acceptedAnswer": { "@type": "Answer", "text": "Ya! Flexa mempunyai sistem tier VIP di mana reseller mendapat diskaun sehingga 10% dan agency mendapat diskaun sehingga 20%. Semakin banyak anda guna, semakin murah harga yang anda dapat." } },
            { "@type": "Question", "name": "AI Tools tu apa?", "acceptedAnswer": { "@type": "Answer", "text": "Flexa dilengkapi AI Tools percuma termasuk: Jana Caption Viral untuk Instagram/TikTok, Content Calendar 30 hari, Strategy Growth customized untuk bisnes anda, dan live AI chat assistant 24/7 untuk bantu anda." } }
          ]
        })}</script>
      </Helmet>

      {/* ───── NAV ───── */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-white/80 backdrop-blur-xl border-b border-black/5 shadow-[0_1px_3px_rgba(0,0,0,0.04)]' : 'bg-white/0 backdrop-blur-none'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <img src="/flexa-wordmark.png" alt="Flexa" width="97" height="28" className="h-7 w-auto object-contain" />
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/real-malaysian-engagement" className="hidden md:inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-600 hover:text-emerald-700 transition-colors" data-testid="nav-real-my">
              🇲🇾 Real MY
              <span className="px-1.5 py-0.5 rounded-full bg-gradient-to-r from-orange-500 to-red-500 text-white text-[9px] font-extrabold leading-none" data-testid="nav-real-my-hot">HOT</span>
            </Link>
            <Link to="/services" className={`hidden md:inline text-sm transition-colors ${scrolled ? 'text-zinc-500 hover:text-zinc-900' : 'text-zinc-500 hover:text-zinc-900'}`} data-testid="nav-services">Harga</Link>
            <a href="#faq" className={`hidden md:inline text-sm transition-colors ${scrolled ? 'text-zinc-500 hover:text-zinc-900' : 'text-zinc-500 hover:text-zinc-900'}`} data-testid="nav-faq">FAQ</a>
            <Link to="/blog" className={`hidden md:inline text-sm transition-colors ${scrolled ? 'text-zinc-500 hover:text-zinc-900' : 'text-zinc-500 hover:text-zinc-900'}`} data-testid="nav-blog">Blog</Link>
            <button
              onClick={() => setLoginOpen(true)}
              data-testid="nav-login-cta"
              className="h-10 px-5 rounded-full bg-zinc-900 text-white text-sm font-semibold hover:bg-zinc-800 hover:scale-[1.03] active:scale-[0.98] transition-all border border-transparent hover:border-violet-500/30 hover:shadow-lg hover:shadow-violet-500/10"
            >
              Log Masuk / Daftar
            </button>
            <MobileMenu />
          </div>
        </div>
      </nav>

      {/* ───── HERO ───── Dirender oleh React HANYA bila hero statik (public/index.html) tiada, cth. navigasi SPA kembali ke "/" */}
      {!staticHero && (
      <section className="relative min-h-[85vh] flex flex-col justify-center overflow-hidden bg-[#FAFAFA]">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-violet-500/5 blur-[120px]" />
        </div>

        {/* Floating Cards, only on lg+ to avoid overlap [FIX #8] */}
        <FloatingCard delay={0} duration={5} className="absolute top-[18%] left-[8%] hidden lg:block">
          <div className="bg-white/90 backdrop-blur-md rounded-2xl px-5 py-3.5 shadow-lg shadow-black/5 border border-zinc-200/60 -rotate-6">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center">
                <Heart className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="text-xs font-bold text-zinc-900">+10,248 Likes</p>
                <p className="text-[10px] text-zinc-400">Delivered in 3 min</p>
              </div>
            </div>
          </div>
        </FloatingCard>

        <FloatingCard delay={1.5} duration={4.5} className="absolute top-[14%] right-[10%] hidden lg:block">
          <div className="bg-white/90 backdrop-blur-md rounded-2xl px-5 py-3.5 shadow-lg shadow-black/5 border border-zinc-200/60 rotate-3">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                <Users className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="text-xs font-bold text-zinc-900">+5,000 Followers</p>
                <p className="text-[10px] text-zinc-400">Instagram - Active</p>
              </div>
            </div>
          </div>
        </FloatingCard>

        <FloatingCard delay={0.8} duration={5.5} className="absolute bottom-[20%] left-[12%] hidden xl:block">
          <div className="bg-white/90 backdrop-blur-md rounded-2xl px-5 py-3.5 shadow-lg shadow-black/5 border border-zinc-200/60 rotate-2">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-emerald-500 to-green-500 flex items-center justify-center">
                <Activity className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="text-xs font-bold text-zinc-900">Status: Aktif</p>
                <p className="text-[10px] text-emerald-500 font-medium">Auto-delivery ON</p>
              </div>
            </div>
          </div>
        </FloatingCard>

        <FloatingCard delay={2} duration={4} className="absolute bottom-[24%] right-[8%] hidden xl:block">
          <div className="bg-white/90 backdrop-blur-md rounded-2xl px-5 py-3.5 shadow-lg shadow-black/5 border border-zinc-200/60 -rotate-3">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center">
                <Eye className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="text-xs font-bold text-zinc-900">100K Views</p>
                <p className="text-[10px] text-zinc-400">TikTok - Completed</p>
              </div>
            </div>
          </div>
        </FloatingCard>

        {/* Hero Content [FIX #2: pt-16 on mobile, pt-24 on sm+] */}
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 text-center pt-16 sm:pt-24">
          <div className="hero-in" style={{ animationDelay: '0ms' }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-50 border border-violet-200/60 mb-8">
              <Zap className="h-3.5 w-3.5 text-violet-600" />
              <span className="text-xs font-semibold text-violet-700 tracking-wide">Panel SMM #1 Malaysia</span>
            </div>
          </div>

          <div className="hero-in" style={{ animationDelay: '60ms' }}>
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-extrabold text-zinc-900 tracking-tighter leading-[1.05] mb-6">
              Bagi content anda{' '}
              <br className="hidden sm:block" />
              <span style={{ fontFamily: serif }} className="italic text-violet-600 font-semibold">momentum yang algoritma nak</span>
            </h1>
          </div>

          <div className="hero-in" style={{ animationDelay: '120ms' }}>
            <p className="text-base sm:text-lg text-zinc-600 max-w-xl mx-auto mb-10 leading-relaxed">
              1,000 view pertama tu yang paling susah. Flexa bagi engagement awal yang buat algoritma push content anda, deliver seawal 60 saat, harga termurah di Malaysia.
            </p>
          </div>

          <div className="hero-in" style={{ animationDelay: '180ms' }}>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/signup" data-testid="hero-cta-primary">
                <button className="h-13 px-8 py-3.5 rounded-full bg-zinc-900 text-white font-semibold text-sm hover:bg-zinc-800 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2 shadow-lg shadow-zinc-900/20 w-full sm:w-auto">
                  Mula Sekarang Percuma <ArrowRight className="h-4 w-4" />
                </button>
              </Link>
              <Link to="/services" data-testid="hero-cta-secondary">
                <button className="h-13 px-8 py-3.5 rounded-full bg-white text-zinc-700 font-semibold text-sm border border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50 transition-colors flex items-center justify-center gap-2 w-full sm:w-auto">
                  Lihat Servis
                </button>
              </Link>
            </div>
            <Link
              to="/real-malaysian-engagement"
              data-testid="hero-real-my-badge"
              className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 border border-emerald-200 hover:border-emerald-400 hover:bg-emerald-100 transition-colors group"
            >
              <span className="text-xs font-bold text-emerald-700">🇲🇾 Real MY: engagement dari orang Malaysia sebenar</span>
              <span className="px-1.5 py-0.5 rounded-full bg-gradient-to-r from-orange-500 to-red-500 text-white text-[9px] font-extrabold">HOT</span>
              <ArrowRight className="h-3.5 w-3.5 text-emerald-600 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>
        </div>

        {/* Scroll indicator [FIX #1] */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 anim-bounce-y">
          <ChevronDown className="h-5 w-5 text-zinc-300" />
        </div>
      </section>
      )}


      {belowFold && (
        <Suspense fallback={<div className="bg-[#09090B] min-h-[60vh]" aria-hidden="true" />}>
          <LandingBelowFold serif={serif} siteSettings={siteSettings} socials={socials} />
        </Suspense>
      )}

      {loginOpen && (
        <Suspense fallback={null}>
          <LoginModal open onClose={() => setLoginOpen(false)} />
        </Suspense>
      )}
    </div>
  );
}
